import type { FC } from 'react'
import { useMemo } from 'react'
import 'styles/index.scss'

import { ConfigProvider } from 'antd'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

import { withProviders } from './app/hocs'
import { useAuth } from './app/providers/auth/AuthProvider'

import { Payment } from './features/Payment'

import Popup from 'common/components/Popup'
import ScrollToTop from 'common/components/ScrollToTop/ScrollToTop'
import { IntakeLockGate } from 'features/Procedure/components/intake-forms-dialog'
import { Generator } from 'features/Generator'
import { Checkout } from 'features/Home/Promotion/Checkout/Checkout'
import { Navigate } from 'react-router'
import { ErrorPage } from './common/components/FallbackComponent/ErrorPage/ErrorPage'
import { AuthIdentity, AuthValidationStatus } from './features/Auth'
import { Auth } from './pages/Auth'
import { Guest } from './pages/Guest'
import { Main } from './pages/Main'
import { SiteRedirect } from './pages/SiteRedirect'
import { PATH_SEGMENT } from './routes/pathSegments'
import { ProtectedRoute } from './routes/ProtectedRoutes/ProtectedRoute'
import { PublicRoute } from './routes/ProtectedRoutes/PublicRoute'
import { generateRouter, PathUtils } from './routes/routes.utils'
import { theme } from './theme'

dayjs.extend(isSameOrAfter)
dayjs.extend(isSameOrBefore)
dayjs.extend(utc)
dayjs.extend(isBetween)
dayjs.extend(timezone)

const App: FC = () => {
  const { user } = useAuth()

  const routesConfig = useMemo(
    () => ({
      [`/${PATH_SEGMENT.LOGIN}/*`]: <Navigate to={PathUtils.getLogin()} />,
      [`/${PATH_SEGMENT.REGISTER}/*`]: <Navigate to={PathUtils.getRegister()} />,
      [`${PATH_SEGMENT.GUEST}/*`]: (
        <PublicRoute>
          <Guest />
        </PublicRoute>
      ),
      [`${PATH_SEGMENT.ERROR}/:code`]: <ErrorPage />,
      [`${PATH_SEGMENT.CODE}/:code`]: <Generator />,
      [PATH_SEGMENT.ROOT]: (
        <ProtectedRoute>
          <SiteRedirect />
        </ProtectedRoute>
      ),
      [`${PATH_SEGMENT.ROOT}:siteId/*`]: (
        <ProtectedRoute>
          <Main />
        </ProtectedRoute>
      ),
      [`/:siteId/${PATH_SEGMENT.CHECKOUT}/*`]: (
        <ProtectedRoute>
          <Payment />
        </ProtectedRoute>
      ),
      [`/:siteId/package/:bookingId/${PATH_SEGMENT.CHECKOUT}/*`]: (
        <ProtectedRoute>
          <Checkout />
        </ProtectedRoute>
      ),
      [PATH_SEGMENT.VALIDATE_IDENTITY]: (
        <ProtectedRoute>
          <AuthIdentity />
        </ProtectedRoute>
      ),
      [PATH_SEGMENT.VALIDATION_PROCESSING]: (
        <ProtectedRoute>
          <AuthValidationStatus />
        </ProtectedRoute>
      ),
      [`${PATH_SEGMENT.AUTH}/*`]: (
        <PublicRoute>
          <Auth />
        </PublicRoute>
      ),
    }),
    [user],
  )

  return (
    <ConfigProvider theme={theme}>
      <Popup />
      <ScrollToTop />
      <IntakeLockGate />
      {generateRouter(routesConfig)}
    </ConfigProvider>
  )
}

export default withProviders(App)
